import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Dialog } from 'radix-ui';
import React, { ReactNode } from 'react';

import { useDialogRegistration } from '~/utils/dialogState';
import { cn } from '~/utils/styles';

interface ModalProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
  disablePortal?: boolean;
  disableAnimation?: boolean;
  description?: string;
  title?: string;
}

interface ModalContentProps {
  children: ReactNode;
  className?: string;
}

const Modal = ({
  open,
  onOpenChange,
  children,
  className,
  disablePortal,
  disableAnimation,
  description,
  title,
  ...props
}: ModalProps) => {
  // Register this modal with global dialog state tracking
  useDialogRegistration(open);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal container={disablePortal ? undefined : document.body}>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/50 backdrop-blur-xs',
            !disableAnimation &&
              'data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed inset-0 z-50 h-full max-h-none w-full max-w-none bg-transparent',
            !disableAnimation &&
              'data-[state=open]:animate-dialog-bouncy-fade data-[state=closed]:animate-dialog-out',
            'focus:outline-hidden',
            className, // <-- moved here
          )}
          {...props}
        >
          {/* DialogTitle is required for accessibility */}
          <Dialog.Title asChild>
            {title ? <span>{title}</span> : <VisuallyHidden.Root>Modal Dialog</VisuallyHidden.Root>}
          </Dialog.Title>
          {description && (
            <Dialog.Description className="sr-only">{description}</Dialog.Description>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const ModalContent = ({ children, className }: ModalContentProps) => {
  return <div className={cn('h-full w-full', className)}>{children}</div>;
};

export { Modal, ModalContent };
